import React, { useState, useEffect, useRef } from "react";
import { convertHeicFilesToJpeg } from "../lib/heicConvert";
import { useLocation, useNavigate } from "react-router-dom";
import { getFullOrderData, clearStoredImages, getPhotoBookData, clearPhotoBookData, restoreStoredImages, clearPhotoBookIDB, loadPhotoBookFromIDB } from "../utils/imageStorage";
import { detectInAppBrowser } from "../lib/inAppBrowser";
import { uploadToSignedUrl, getPhotoUploadTokens, confirmUploadedPhotos, uploadFilesToStorage, runWithConcurrency, UPLOAD_CONCURRENCY } from "../lib/storageUpload";
import Header from "../components/Header";
import { useLanguage } from "../context/LanguageContext";
import "./OrderForm.css";

// SVG progress ring geometry - keep in sync with the r value in the JSX/CSS
const PROGRESS_RING_RADIUS = 54;
const PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RING_RADIUS;

// Backend base URL - same localhost-detection used throughout this file's
// upload/checkout calls, extracted so it's also reachable from the
// mount-time settings fetch (not just from inside handleSubmit).
const getApiUrl = () => {
  const hostname = window.location.hostname.toLowerCase();
  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "" ||
    hostname.includes("localhost");
  return isLocalhost ? "http://localhost:5000" : "https://orders.fletaime.com";
};

// Compress image using Canvas API - reduces memory usage on mobile
const compressImageFile = (file, maxDimension = 2000, quality = 0.88) => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    // img.decode() guarantees the image is FULLY decoded before we draw it.
    // img.onload only guarantees the resource was fetched, not that decoding
    // for painting is complete - under memory pressure (large albums,
    // 74-150 photos) this let some images get drawn as a blank/undecoded
    // frame, which canvas.toBlob then exported as a solid black JPEG.
    img.decode().then(() => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      // Free image memory immediately after drawing
      img.src = '';
      canvas.toBlob(
        (blob) => {
          // Free canvas memory explicitly
          canvas.width = 0;
          canvas.height = 0;
          if (!blob) { resolve(file); return; }
          const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
          resolve(compressed);
        },
        'image/jpeg',
        quality
      );
    }).catch(() => { URL.revokeObjectURL(url); resolve(file); });
  });
};

// Helper function to convert file to base64
const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Compress a blob: URL image to a print-quality JPEG Blob (max 2600px, 92%
// quality - raised from 2000px/88% to better match the server's print
// resolution/quality, see CONFIG.PDF_RESOLUTION_SCALE/PDF_IMAGE_QUALITY in
// orderController.js which must move together with this - while still
// bounding memory usage on mobile, since going further risks reintroducing
// the crashes/blank-page bugs large uncompressed uploads used to cause; see
// order_upload_storage_migration memory). Same sizing as the old base64
// path, just stopping short of the base64-encoding step, since these now
// upload as raw bytes directly to Storage instead of being embedded in the
// /finalize JSON body - see order_upload_storage_migration memory.
const compressBlobUrlToJpegBlob = (blobUrl) => new Promise((resolve) => {
  const img = new Image();
  img.src = blobUrl;
  // See compressImageFile's note - img.decode() guarantees full decode
  // before drawing, unlike img.onload, closing the race that produced
  // solid-black PhotoBook pages on large albums.
  img.decode().then(() => {
    const maxDim = 2600;
    let { width, height } = img;
    if (width > maxDim || height > maxDim) {
      if (width > height) { height = Math.round((height / width) * maxDim); width = maxDim; }
      else { width = Math.round((width / height) * maxDim); height = maxDim; }
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    img.src = '';
    canvas.toBlob((blob) => {
      canvas.width = 0;
      canvas.height = 0;
      resolve(blob);
    }, 'image/jpeg', 0.92);
  }).catch((error) => {
    console.error('Error compressing blob URL:', error);
    resolve(null);
  });
});

// Build the cover pages structure (front/back, 33x48cm) with each slot's src
// left as-is (blob:/data:) - the actual upload happens in one combined pass
// together with regular page slots, see uploadAllPageImages below.
const buildCoverPages = (coverData) => {
  if (!coverData) return [];
  const pages = [];

  if (coverData.mode === 'uploaded') {
    if (coverData.uploaded?.front?.src) {
      pages.push({ isCover: true, coverSide: 'front', layout: 'full',
        backgroundColor: coverData.style?.bg || '#f6f4ee', images: { 0: { ...coverData.uploaded.front } } });
    }
    if (coverData.uploaded?.back?.src) {
      pages.push({ isCover: true, coverSide: 'back', layout: 'full',
        backgroundColor: coverData.style?.bg || '#f6f4ee', images: { 0: { ...coverData.uploaded.back } } });
    }
  } else {
    const frontImages = {};
    (coverData.frontSlots || []).forEach((slot, i) => { if (slot?.src) frontImages[i] = { ...slot }; });
    const backImages = {};
    (coverData.backSlots || []).forEach((slot, i) => { if (slot?.src) backImages[i] = { ...slot }; });
    pages.push({ isCover: true, coverSide: 'front', layout: coverData.frontLayout || 'full',
      backgroundColor: coverData.style?.bg || '#f6f4ee', images: frontImages,
      meta: coverData.meta, style: coverData.style, stickers: coverData.frontStickers || [] });
    // Shpina (page 2) - tekst (Titulli/Data) + një sticker i vetëm statik
    // opsional, gjithmonë i centruar (jo drag/resize si te frontStickers/
    // backStickers - shih .cover-spine-sticker te CoverEditor.js). Pika
    // (46,46,8,8) e vendos të centruar në kutinë e plotë A4 që
    // generatePhotoBookPages përdor për çdo faqe, duke përputhur pozicionin
    // e shiritit të hollë të shpinës në editor (i qendërzuar rreth 50%).
    pages.push({ isCover: true, coverSide: 'spine', layout: 'spine',
      backgroundColor: coverData.style?.bg || '#f6f4ee', images: {},
      meta: coverData.meta, style: coverData.style,
      stickers: coverData.spineSticker
        ? [{ svgUrl: coverData.spineSticker.svgUrl, x: 46, y: 46, width: 8, height: 8 }]
        : [] });
    pages.push({ isCover: true, coverSide: 'back', layout: coverData.backLayout || 'full',
      backgroundColor: coverData.style?.bg || '#f6f4ee', images: backImages,
      meta: coverData.meta, style: coverData.style, stickers: coverData.backStickers || [] });
  }
  return pages;
};

// A non-blob src is only legitimately usable as-is if it's an already-hosted
// URL or a real (non-truncated) data URI - anything else is corrupted data
// that would ship as a blank page if passed through silently.
const isValidNonBlobSrc = (src) => {
  if (!src) return false;
  if (/^https?:\/\//i.test(src)) return true;
  if (src.startsWith('data:')) {
    const commaIdx = src.indexOf(',');
    const data = commaIdx >= 0 ? src.slice(commaIdx + 1) : src;
    return data.length >= 100;
  }
  return false;
};

// Uploads every blob-URL image across the PhotoBook's pages directly to
// Supabase Storage instead of converting them to base64 and embedding them
// in one giant /finalize request - this is what was actually causing the
// multi-minute wait (and the associated black-photo/blank-page failures),
// not PDF generation itself, which only takes a few seconds server-side -
// see order_upload_storage_migration memory. Each slot's `src` becomes the
// resulting public URL; data:/already-URL sources pass through unchanged
// (never mutates the original page objects, since the customer could still
// navigate back to the page editor while this runs). A failed slot is
// print-critical (it would ship as a blank page), so failures are reported
// back rather than silently skipped.
const uploadAllPageImages = async (apiUrl, orderId, sourcePages, onProgress, onUploadProgress) => {
  const allPages = sourcePages.map(page => ({ ...page, images: { ...page.images } }));

  const failedConversions = [];
  const uploadTasks = []; // { pageIndex, slotIndex, fileName }
  const blobByFileName = new Map();

  let totalToConvert = 0;
  for (const page of allPages) {
    for (const image of Object.values(page.images || {})) {
      if (image?.src?.startsWith('blob:')) totalToConvert++;
    }
  }

  let converted = 0;
  for (let pageIndex = 0; pageIndex < allPages.length; pageIndex++) {
    const page = allPages[pageIndex];
    for (const [slotIndex, image] of Object.entries(page.images || {})) {
      if (image?.src?.startsWith('blob:')) {
        // handled below
      } else if (isValidNonBlobSrc(image?.src)) {
        continue; // already a usable URL/data URI - pass through unchanged
      } else {
        // A slot key exists but its src isn't blob:/http(s):/a real data URI -
        // this used to happen silently (a since-removed edge case where a
        // shared photo's blob URL got revoked while still placed on another
        // page - see photobook_shared_photo_blob_bug memory) and shipped a
        // blank printed page with zero warning. Treat it the same as a
        // failed conversion so the existing block-and-alert safety net
        // catches it, instead of quietly passing the bad value through to
        // the server (which is what let 18 pages come back blank white on a
        // real order - see order_upload_storage_migration memory).
        console.error(
          `PhotoBook page ${pageIndex} slot ${slotIndex}: invalid src (type=${typeof image?.src}, ` +
          `value=${JSON.stringify(image?.src)?.slice(0, 200)}), full image object:`,
          image
        );
        failedConversions.push({ pageIndex, slotIndex, name: image?.name });
        continue;
      }

      const blob = await compressBlobUrlToJpegBlob(image.src);
      converted++;
      if (onProgress) onProgress(converted, totalToConvert);
      // Small delay to allow GC to free canvas memory between conversions
      await new Promise(resolve => setTimeout(resolve, 30));

      if (!blob) {
        failedConversions.push({ pageIndex, slotIndex, name: image.name });
        continue;
      }
      const fileName = `page_${pageIndex}_${slotIndex}_${Date.now()}.jpg`;
      uploadTasks.push({ pageIndex, slotIndex, fileName });
      blobByFileName.set(fileName, blob);
    }
  }

  const uploadResults = await uploadFilesToStorage(
    apiUrl,
    orderId,
    uploadTasks.map(t => ({ fileName: t.fileName, blob: blobByFileName.get(t.fileName) })),
    { onProgress: onUploadProgress }
  );

  for (const task of uploadTasks) {
    const url = uploadResults.get(task.fileName);
    const page = allPages[task.pageIndex];
    const existingImage = page.images[task.slotIndex];
    if (url) {
      page.images[task.slotIndex] = { ...existingImage, src: url };
    } else {
      failedConversions.push({ pageIndex: task.pageIndex, slotIndex: task.slotIndex, name: existingImage?.name });
    }
  }

  return { allPages, failedConversions };
};

// Small brand marks shown on the "Kartë Online" payment option, so it reads
// as a real card-payment method rather than a bare label.
const VisaMark = () => (
  <svg width="30" height="20" viewBox="0 0 30 20" aria-label="Visa">
    <rect width="30" height="20" rx="3" fill="#1A1F71" />
    <text x="15" y="14" textAnchor="middle" fontFamily="Georgia, serif" fontStyle="italic" fontWeight="700" fontSize="9" fill="#ffffff">VISA</text>
  </svg>
);

const MastercardMark = () => (
  <svg width="30" height="20" viewBox="0 0 30 20" aria-label="Mastercard">
    <rect width="30" height="20" rx="3" fill="#F4F6F8" />
    <circle cx="12.5" cy="10" r="6" fill="#EB001B" />
    <circle cx="17.5" cy="10" r="6" fill="#F79E1B" />
    <path d="M15 5.3a6 6 0 0 1 0 9.4 6 6 0 0 1 0-9.4Z" fill="#FF5F00" />
  </svg>
);

const ApplePayMark = () => (
  <svg width="34" height="20" viewBox="0 0 34 20" aria-label="Apple Pay">
    <rect width="34" height="20" rx="3" fill="#000000" />
    <text x="17" y="14" textAnchor="middle" fontFamily="-apple-system, sans-serif" fontWeight="600" fontSize="8.5" fill="#ffffff"> Pay</text>
  </svg>
);

const OrderForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  
  // Merr orderData nga imageStorage (jo nga location.state direkt)
  // Kjo shmang memory issues në mobile kur ka shumë foto
  const [orderData, setOrderData] = useState(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    country: "",
    city: "",
    address: "",
    phone: "",
    comments: "",
  });

  const getShippingCost = (country) => {
    if (country === 'Kosovë') return 2;
    if (country === 'Shqipëri' || country === 'Maqedoni e Veriut') return 5;
    return 0;
  };

  const [errors, setErrors] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("cash"); // "cash" | "card"
  const [whatsappUrl, setWhatsappUrl] = useState(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [orderPhotoUrl, setOrderPhotoUrl] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageLinks, setImageLinks] = useState([]);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [uploadStage, setUploadStage] = useState(''); // 'preparing', 'converting', 'uploading', 'finalizing'
  const [failedPhotos, setFailedPhotos] = useState([]);
  const [inAppBrowser] = useState(() => detectInAppBrowser());

  // Synchronous lock against a fast double-tap on the submit button starting
  // two overlapping submissions (React's `uploading` state only disables the
  // button after a re-render, which isn't instant on slower phones).
  const submittingRef = useRef(false);
  // Set synchronously (not via setState) right before an intentional
  // window.location.href redirect (e.g. to Stripe), so the beforeunload
  // handler below - which reads it directly rather than through a state
  // closure - sees the change immediately instead of racing React's batching.
  const skipUnloadWarningRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);

  const [promotionCode, setPromotionCode] = useState('');
  const [discount, setDiscount] = useState(0); // 0.1 = 10%
  const [promoValid, setPromoValid] = useState(null); // null, true, false
  const [promoMessage, setPromoMessage] = useState('');

  const PROMO_CODES = { '': 15 };

  // Automatic incentive for paying online instead of cash-on-delivery —
  // stacks on top of any promo code, since they're separate, independent
  // reasons for a discount (one from a marketing code, one from the
  // customer's own payment choice). Admin-managed from /dashboard/discounts;
  // defaults to "off" until the real value loads so nothing is ever
  // over-promised (see the settings fetch further down).
  const [onlineDiscount, setOnlineDiscount] = useState({ enabled: false, percent: 0 });

  const getEffectiveDiscount = () => discount + (paymentMethod === 'card' && onlineDiscount.enabled ? onlineDiscount.percent / 100 : 0);

  const getDiscountedProductPrice = () => {
    const base = orderData?.totalPrice || 0;
    return parseFloat((base * (1 - getEffectiveDiscount())).toFixed(2));
  };

  const handlePromoCode = () => {
    const code = promotionCode.trim().toLowerCase();
    if (!code) {
      setPromoValid(null);
      setPromoMessage('');
      setDiscount(0);
      return;
    }
    if (PROMO_CODES[code] !== undefined) {
      const pct = PROMO_CODES[code];
      setDiscount(pct / 100);
      setPromoValid(true);
      setPromoMessage(t('order.promo.applied', { pct }));
    } else {
      setDiscount(0);
      setPromoValid(false);
      setPromoMessage(t('order.promo.invalid'));
    }
  };

  // Merr cilësimin aktual të zbritjes për pagesë online (i menaxhuar nga
  // admini te /dashboard/discounts). Dështimi i heshtur (mbetet "off") është
  // i qëllimshëm - nuk duam kurrë të premtojmë një zbritje që s'e kemi konfirmuar.
  useEffect(() => {
    fetch(`${getApiUrl()}/api/settings`)
      .then(r => r.json())
      .then(data => {
        if (data && !data.error) {
          setOnlineDiscount({
            enabled: !!data.online_discount_enabled,
            percent: data.online_discount_percent ?? 0,
          });
        }
      })
      .catch(() => {});
  }, []);

  // Ngarko orderData nga imageStorage kur komponenti montohet
  useEffect(() => {
    const hasStoredOrder = location.state?.hasStoredOrder;
    const usePhotoBookStorage = location.state?.usePhotoBookStorage;

    if (usePhotoBookStorage) {
      const photoBookData = getPhotoBookData();
      setOrderData(photoBookData ? { ...location.state, ...photoBookData } : location.state);
      const timer = setTimeout(() => setIsLoading(false), 100);
      return () => clearTimeout(timer);
    }

    if (hasStoredOrder) {
      const storedOrderData = getFullOrderData();
      if (storedOrderData) setOrderData(storedOrderData);
      const timer = setTimeout(() => setIsLoading(false), 100);
      return () => clearTimeout(timer);
    }

    if (location.state && !location.state.hasStoredOrder) {
      setOrderData(location.state);
      const timer = setTimeout(() => setIsLoading(false), 100);
      return () => clearTimeout(timer);
    }

    // Faqja u rifreskua (nuk ka location.state) – provo IDB për të dyja rastet
    let cancelled = false;
    (async () => {
      // Rasti 1: porosi normale me foto
      const regular = await restoreStoredImages();
      if (regular) {
        if (!cancelled) setOrderData({ ...regular.metadata, uploadedImages: regular.images });
        if (!cancelled) setIsLoading(false);
        return;
      }
      // Rasti 2: PhotoBook – restauro faqet dhe fotot nga IDB
      const pb = await loadPhotoBookFromIDB();
      if (pb && !cancelled) {
        const totalPhotosInPages = pb.pages.reduce(
          (n, p) => n + Object.keys(p.images || {}).length, 0
        );
        setOrderData({
          product: pb.productData,
          totalPrice: pb.productData?.totalPrice || pb.productData?.price || 24.90,
          selectedPages: pb.productData?.selectedPages,
          isPhotoBook: true,
          pages: pb.pages,
          uploadedPhotos: pb.uploadedPhotos,
          photosInPages: totalPhotosInPages,
          coverData: pb.coverData || null,
        });
      }
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [location.state]);

  // Redirect to home if no order data after loading
  useEffect(() => {
    if (!isLoading && !orderData && !uploading) {
      navigate("/");
    }
  }, [isLoading, orderData, uploading, navigate]);

  // Prevent user from accidentally closing the page during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading && !skipUnloadWarningRef.current) {
        e.preventDefault();
        e.returnValue = t('order.beforeUnloadWarning');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploading, t]);

  // Show loading state while checking for order data
  if (isLoading) {
    return (
      <div style={{padding: '50px', textAlign: 'center'}}>
        <h2>{t('order.loadingOrder')}</h2>
      </div>
    );
  }

  // Show error if no order data after loading
  if (!orderData && !uploading) {
    return (
      <div style={{padding: '50px', textAlign: 'center'}}>
        <h2>{t('order.noData')}</h2>
        <button onClick={() => navigate("/")}>{t('order.backToStart')}</button>
      </div>
    );
  }

  // "Ngarkimi Manual" - porositë e këtij produkti nuk dërgohen në WhatsApp,
  // vetëm gjenerohet linku i fotove dhe shfaqet faqja e faleminderimit
  const isManualUploadOrder = orderData?.product?.id === 'upload-special';

  const handleCopyPhotoLink = async () => {
    if (!orderPhotoUrl) return;
    try {
      await navigator.clipboard.writeText(orderPhotoUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy photo link:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('order.validation.firstName');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('order.validation.lastName');
    }

    if (!formData.country) {
      newErrors.country = t('order.validation.country');
    }

    if (!formData.city.trim()) {
      newErrors.city = t('order.validation.city');
    }

    if (!formData.address.trim()) {
      newErrors.address = t('order.validation.address');
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t('order.validation.phone');
    } else if (!/^[\d\s+\-()]+$/.test(formData.phone)) {
      newErrors.phone = t('order.validation.phoneInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fetch with timeout - extended for large image uploads (100+ photos)
  const fetchWithTimeout = async (
    url,
    options,
    timeout = 600000 // 10 minutes for 100+ photos
  ) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  // A transient network/connection drop (mobile network switching WiFi<->cellular,
  // brief signal loss, iOS Safari suspending a backgrounded tab mid-upload, proxy
  // idle-timeout, etc). Safari reports these as "TypeError: Load failed" - Chrome
  // says "Failed to fetch" - so both wordings (and AbortError from our own timeout)
  // must be treated as retryable, or every blip forces the customer to redo the
  // whole multi-minute upload from scratch.
  const isRetryableNetworkError = (err) =>
    err.name === "AbortError" ||
    (err.name === "TypeError" &&
      /fetch|load failed|network/i.test(err.message || ""));

  // /finalize only writes order.json and (for PhotoBook) regenerates a PDF -
  // both idempotent overwrites keyed by orderId - so retrying it is safe,
  // unlike /init which would create a duplicate Supabase order row on retry.
  const postJsonWithRetry = async (url, body, timeout, maxAttempts = 2) => {
    let lastErr;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetchWithTimeout(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }, timeout);
        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          // Server errors carry a customer-readable Albanian message in JSON
          // ({success:false, message:"..."}) - surface that text directly
          // instead of a raw "Server error 400: {json}" blob.
          let cleanMessage = "";
          try { cleanMessage = JSON.parse(errorText)?.message || ""; } catch (_) {}
          const err = new Error(cleanMessage || `Server error ${response.status}: ${errorText}`);
          err.status = response.status;
          throw err;
        }
        return response;
      } catch (err) {
        lastErr = err;
        const retryable = isRetryableNetworkError(err) || (err.status && err.status >= 500);
        if (!retryable || attempt === maxAttempts) throw err;
        await new Promise(resolve => setTimeout(resolve, attempt * 1000));
      }
    }
    throw lastErr;
  };

  const BATCH_UPLOAD_SIZE = 12;
  const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current) return;
    if (!validateForm()) {
      return;
    }

    submittingRef.current = true;
    setUploading(true);
    setUploadProgress(0);
    setUploadStage('preparing');
    setFailedPhotos([]);

    // Merr numrin total të fotove për t'u shfaqur në preloader
    const photoCount = orderData.uploadedImages?.length || 0;
    setTotalPhotos(photoCount);

    try {
      const API_URL = getApiUrl();

      // Check if this is a PhotoBook order with pages
      const isPhotoBook = orderData.isPhotoBook && orderData.pages;

      const shippingCost = getShippingCost(formData.country);
      const discountedProductPrice = getDiscountedProductPrice();
      const discountedTotal = parseFloat((discountedProductPrice + shippingCost).toFixed(2));

      // Step 1: /init - creates the order, no photos yet. Single attempt (not
      // retried) since retrying it would create a duplicate Supabase order row;
      // a real failure here is rare (tiny JSON body, no Blob parts) and the
      // customer can just resubmit the form.
      setUploadProgress(5);
      const initResponse = await fetchWithTimeout(`${API_URL}/upload-order/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emri: formData.firstName,
          mbiemri: formData.lastName,
          vendi: formData.city,
          adresa: formData.address,
          telefoni: formData.phone,
          komentet: formData.comments || "",
          emriProduktit: orderData.product?.name || "Produkt",
          cmimi: discountedTotal,
          shteti: formData.country,
          faqet: orderData.selectedPages
            ? (orderData.selectedPages.label || `${orderData.selectedPages.pages} faqe`)
            : undefined,
          numriFotove: isPhotoBook ? (orderData.photosInPages || 0) : photoCount,
          isPhotoBook: !!isPhotoBook,
          kodPromocional: discount > 0 ? promotionCode.trim() : undefined,
          zbritja: getEffectiveDiscount() > 0 ? `${Math.round(getEffectiveDiscount() * 100)}%` : undefined,
          gjuha: lang,
          orderSource: "website",
        }),
      }, 30000);

      if (!initResponse.ok) {
        const errorText = await initResponse.text().catch(() => "");
        throw new Error(t('order.error.initFailed', { status: initResponse.status, text: errorText }));
      }
      const initData = await initResponse.json();
      if (!initData.success) {
        throw new Error(t('order.error.initFailedGeneric'));
      }
      const { orderId, supabaseOrderId } = initData;

      setUploadProgress(10);

      let allPages = null;
      let filesToUpload = [];

      if (isPhotoBook) {
        // PhotoBook order - upload each page's photos straight to Supabase
        // Storage instead of converting everything to base64 and sending it
        // all in one giant /finalize request (that giant request, not PDF
        // generation, was the real source of the multi-minute wait - see
        // order_upload_storage_migration memory).
        setUploadStage('converting');
        setTotalPhotos(orderData.photosInPages || 0);

        // If cover personalization: cover pages (33x48cm) go first, same
        // order as before
        const coverPages = orderData.coverData ? buildCoverPages(orderData.coverData) : [];
        const combinedPages = [...coverPages, ...orderData.pages];

        const { allPages: uploadedPages, failedConversions } = await uploadAllPageImages(
          API_URL,
          orderId,
          combinedPages,
          (processed, total) => {
            // Progress 10% to 30% during conversion
            const progress = 10 + (processed / total) * 20;
            setUploadProgress(Math.round(progress));
          },
          (done, total) => {
            // Progress 30% to 60% while page photos upload to Storage - this
            // used to show no movement at all (stuck at 40%) during what is
            // actually the longest part of a PhotoBook submit.
            const progress = 30 + (done / Math.max(total, 1)) * 30;
            setUploadProgress(Math.round(progress));
          }
        );

        if (failedConversions.length > 0) {
          console.error('Photo upload failed for slots:', failedConversions);
          alert(t('order.alert.uploadFailedSlots', { count: failedConversions.length }));
          setUploading(false);
          setUploadProgress(0);
          setUploadStage('');
          return;
        }

        allPages = uploadedPages;

        // Original photo files, uploaded via /batch just like a regular order
        filesToUpload = (orderData.uploadedPhotos || [])
          .filter(p => p.file)
          .map(p => p.file);

        setUploadStage('uploading');
      } else {
        // Progress: 10-15% - Konvertimi i HEIC
        setUploadStage('converting');

        const originalFiles = (orderData.uploadedImages || [])
          .filter(img => img.file)
          .map(img => img.file);
        const heicConverted = await convertHeicFilesToJpeg(originalFiles);

        setUploadProgress(15);
        setUploadStage('uploading');

        // Kompreso fotot me Canvas API - njëra nga njëra me delay për GC
        for (let i = 0; i < heicConverted.length; i++) {
          const compressed = await compressImageFile(heicConverted[i]);
          filesToUpload.push(compressed);
          // Progress gradual nga 15% deri në 40% (kompresimi, jo dërgimi)
          const progress = 15 + ((i + 1) / heicConverted.length) * 25;
          setUploadProgress(Math.round(progress));
          // Small delay to allow GC to free canvas/image memory
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      // Step 2: /batch - upload photos in small groups instead of one giant
      // request. Each batch's FormData is built fresh (buildForm) on every
      // retry attempt, which is what makes retrying safe - see
      // postFormDataWithRetry's note.
      const photoNames = [];
      const orderFailedPhotos = [];
      const chunks = chunkArray(filesToUpload, BATCH_UPLOAD_SIZE);
      let uploadedSoFar = 0;
      // PhotoBook orders have already used 10-60% on page-photo upload; the
      // reference-copy photos then fill 60-90%. Regular orders go 40-90%.
      const batchProgressBase = isPhotoBook ? 60 : 40;

      for (let c = 0; c < chunks.length; c++) {
        const chunk = chunks[c];
        const startIndex = uploadedSoFar;
        // Each photo uploads directly to Supabase Storage (not through this
        // server) - see order_upload_storage_migration memory. For PhotoBook
        // orders this is only the backup/reference copy in
        // order_details/order.json - the actual printed pages already have
        // their images embedded via pagesData, independent of this upload.
        const fileNames = chunk.map((file, i) => {
          const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
          return `${supabaseOrderId}_${Date.now()}_${startIndex + i}.${ext}`;
        });

        try {
          const tokens = await getPhotoUploadTokens(API_URL, orderId, fileNames);
          const tokenByFileName = new Map(tokens.map(t => [t.fileName, t]));
          // Collected as {path, i} so the confirmed order matches the
          // customer's original photo order even with concurrent uploads.
          const uploadedPathEntries = [];
          let doneInChunk = 0;

          await runWithConcurrency(chunk, UPLOAD_CONCURRENCY, async (file, i) => {
            const fileName = fileNames[i];
            const tokenInfo = tokenByFileName.get(fileName);
            if (!tokenInfo) {
              orderFailedPhotos.push({ originalName: file?.name || fileName, reason: "token_deshtoi" });
            } else {
              try {
                await uploadToSignedUrl(tokenInfo.signedUrl, file);
                uploadedPathEntries.push({ path: tokenInfo.path, i });
              } catch (fileErr) {
                // A single photo that fails even after retries used to abort
                // the WHOLE order here, forcing the customer to redo
                // everything (including, for PhotoBook orders, re-laying-out
                // pages) even if most photos had already gone through. Skip
                // just this one and keep going - see order_upload_resilience memory.
                console.error(`Photo ${startIndex + i + 1} failed after retries:`, fileErr);
                orderFailedPhotos.push({ originalName: file?.name || fileName, reason: "lidhje_e_paqendrueshme" });
              }
            }
            // Per-photo progress within the batch range, instead of one jump
            // per 12-photo batch.
            doneInChunk++;
            const doneOverall = startIndex + doneInChunk;
            const progress = batchProgressBase +
              (doneOverall / Math.max(filesToUpload.length, 1)) * (90 - batchProgressBase);
            setUploadProgress(Math.round(progress));
          });

          if (uploadedPathEntries.length > 0) {
            uploadedPathEntries.sort((a, b) => a.i - b.i);
            const urls = await confirmUploadedPhotos(
              API_URL, orderId, supabaseOrderId, uploadedPathEntries.map(e => e.path)
            );
            photoNames.push(...urls);
          }
        } catch (batchErr) {
          console.error(`Batch ${c + 1}/${chunks.length} failed:`, batchErr);
          chunk.forEach(f => orderFailedPhotos.push({ originalName: f.name, reason: "lidhje_e_paqendrueshme" }));
        }

        uploadedSoFar += chunk.length;
        const progress = batchProgressBase +
          (uploadedSoFar / Math.max(filesToUpload.length, 1)) * (90 - batchProgressBase);
        setUploadProgress(Math.round(progress));
      }

      if (photoNames.length === 0 && filesToUpload.length > 0) {
        throw new Error(t('order.error.noPhotosUploaded'));
      }

      setFailedPhotos(orderFailedPhotos);

      // Step 3: /finalize - PDF generation (PhotoBook) / order.json. Safe to
      // retry: it only overwrites files keyed by orderId, no new Supabase row.
      setUploadStage('finalizing');
      setUploadProgress(92);

      const finalizeResponse = await postJsonWithRetry(
        `${API_URL}/upload-order/finalize`,
        {
          orderId,
          supabaseOrderId,
          photoNames,
          isPhotoBook: !!isPhotoBook,
          pagesData: isPhotoBook ? JSON.stringify(allPages) : undefined,
        },
        300000, // 5 minutes - PDF generation on large PhotoBooks can take a while
        2
      );

      setUploadProgress(99);

      const data = await finalizeResponse.json();

      if (!data.success) {
        alert(t('order.alert.saveFailed'));
        setUploading(false);
        return;
      }

      // Merr orderUrl dhe numrin e fotove nga backend
      const orderUrl = data.orderUrl;
      const zipUrl = data.zipUrl; // ZIP of per-page A4 JPGs, for PhotoBook orders
      const numPhotos = data.photoUrls
        ? data.photoUrls.length
        : (orderData.isPhotoBook ? orderData.photosInPages : (orderData.uploadedImages?.length || 0));

      // "Ngarkimi Manual" - mos e dërgo porosinë në WhatsApp, vetëm gjenero
      // linkun e fotove dhe shfaq faqen e faleminderimit
      if (isManualUploadOrder) {
        setUploadProgress(100);
        await new Promise(resolve => setTimeout(resolve, 500));

        clearStoredImages();
        clearPhotoBookData();
        clearPhotoBookIDB();

        setOrderPhotoUrl(orderUrl);
        setOrderSubmitted(true);
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      // Pagesë online - kalo te Stripe Checkout në vend të WhatsApp-it.
      // Porosia dhe fotot janë ruajtur tashmë (init/batch/finalize më sipër);
      // statusi i pagesës shënohet "paid" vetëm nga webhook-u i Stripe-it,
      // jo nga ky redirect, kështu që s'ka rrezik dikush ta shënojë porosinë
      // të paguar pa paguar realisht.
      if (paymentMethod === "card") {
        setUploadStage('finalizing');
        const checkoutResponse = await fetchWithTimeout(`${API_URL}/payment/create-checkout-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ supabaseOrderId, amount: discountedTotal, currency: "eur" }),
        }, 30000);

        const checkoutData = await checkoutResponse.json();
        if (!checkoutResponse.ok || !checkoutData.success || !checkoutData.url) {
          throw new Error(checkoutData.message || t('order.error.checkoutFailed'));
        }

        clearStoredImages();
        clearPhotoBookData();
        clearPhotoBookIDB();

        // setUploading(false) alone isn't enough here: it's a React state
        // update, and window.location.href fires the native beforeunload
        // event synchronously, before React re-renders and re-registers the
        // handler with the new value - so the OLD handler (still closing
        // over uploading=true) would catch it and show a "Leave site?"
        // prompt on our own intentional redirect. The ref bypasses that race.
        skipUnloadWarningRef.current = true;
        setUploading(false);
        setUploadProgress(0);

        window.location.href = checkoutData.url;
        return;
      }

      // Ndërto mesazhin WhatsApp
      const shippingCostFinal = getShippingCost(formData.country);
      const discountedProductPriceFinal = getDiscountedProductPrice();
      const totalWithShippingFinal = parseFloat((discountedProductPriceFinal + shippingCostFinal).toFixed(2));

      let message = `${t('wa.title')}\n\n`;
      message += `${t('wa.product')} ${orderData.product?.name || 'Produkt'}\n`;
      if (discount > 0) {
        message += `${t('wa.priceBefore')} ${orderData.totalPrice || 0}€\n`;
        message += `${t('wa.promoCode')} ${promotionCode.trim()} (-${discount * 100}%)\n`;
        message += `${t('wa.priceAfterDiscount')} ${discountedProductPriceFinal}€\n`;
      } else {
        message += `${t('wa.price')} ${orderData.totalPrice || 0}€\n`;
      }
      message += `${t('wa.shipping', { country: formData.country })} +${shippingCostFinal}€\n`;
      message += `${t('wa.totalPrice')} ${totalWithShippingFinal}€\n\n`;

      // Shfaq detajet sipas llojit të porosisë
      if (orderData.selectedPages) {
        message += `${t('wa.bookDetails')}\n`;
        message += `${t('wa.bookWith')} ${orderData.selectedPages?.label || orderData.selectedPages?.pages + ' ' + t('wa.pagesWord')}\n`;
        message += `${t('wa.photosInPage')} ${numPhotos}\n\n`;
      } else {
        // Për produktet që nuk janë libra (si Photo Frame)
        message += `${t('wa.productDetails')}\n`;
        message += `${t('wa.photosCount')} ${numPhotos}\n\n`;
      }

      message += `${t('wa.customerData')}\n`;
      message += `${t('wa.name')} ${formData.firstName} ${formData.lastName}\n`;
      message += `${t('wa.country')} ${formData.country}\n`;
      message += `${t('wa.city')} ${formData.city}\n`;
      message += `${t('wa.address')} ${formData.address}\n`;
      message += `${t('wa.phone')} ${formData.phone}\n`;
      if (formData.comments && formData.comments.trim()) {
        message += `${t('wa.comment')} ${formData.comments}\n`;
      }
      message += `\n`;

      // Different message for PhotoBook or regular orders
      if (orderData.isPhotoBook && zipUrl) {
        message += `${t('wa.bookPagesZip')}\n`;
        message += `${t('wa.downloadPages')} ${zipUrl}\n\n`;
      } else if (numPhotos > 0 && orderUrl) {
        message += `${t('wa.customerPhotos')}\n`;
        message += `${t('wa.seePhotos')} ${orderUrl}\n\n`;
      }

      message += t('wa.waitingConfirm');
      message += `\n${t('wa.orderDate')} ${new Date().toLocaleDateString()}`;

      // Encode message for WhatsApp URL
      const encodedMessage = encodeURIComponent(message);
      const finalWhatsappUrl = `https://api.whatsapp.com/send?phone=38345937644&text=${encodedMessage}`;

      setUploadProgress(100);
      
      // Short delay to show 100% before closing preloader
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Pastro fotot e ruajtura për të liruar memorien
      clearStoredImages();

      // Pastro PhotoBook data nga memory storage dhe IndexedDB
      clearPhotoBookData();
      clearPhotoBookIDB();

      setWhatsappUrl(finalWhatsappUrl);
      setOrderSubmitted(true);
      setUploading(false);
      setUploadProgress(0);

      // Open WhatsApp automatically - optimized for both mobile and desktop
      // Use a small delay to ensure the success message is visible first
      setTimeout(() => {
        try {
          // Detect if it's a mobile device
          const isMobile =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent
            );

          if (isMobile) {
            // For mobile devices, use window.location.href for better compatibility
            // This redirects the entire page to WhatsApp, which works reliably on mobile browsers
            window.location.href = finalWhatsappUrl;
          } else {
            // For desktop, use window.open which opens in a new tab
            window.open(finalWhatsappUrl, "_blank", "noopener,noreferrer");
          }
        } catch (error) {
          console.error("Error opening WhatsApp:", error);
          // Fallback: try window.location as last resort
          try {
            window.location.href = finalWhatsappUrl;
          } catch (fallbackError) {
            console.error("Fallback also failed:", fallbackError);
          }
        }
      }, 500); // Small delay to show success message before redirect
    } catch (err) {
      console.error("Error submitting order:", err);
      console.error("Error details:", {
        name: err.name,
        message: err.message,
        stack: err.stack,
      });

      let errorMessage = t('order.error.submitFailed');

      // Provide more specific error messages. Note: Safari reports fetch network
      // failures as "TypeError: Load failed" (no "fetch" substring), while Chrome
      // says "Failed to fetch" - both must be detected here or Safari users just
      // see the raw "Load failed" text with no useful explanation.
      if (isRetryableNetworkError(err)) {
        if (err.name === "AbortError") {
          errorMessage = t('order.error.timeout');
        } else {
          errorMessage = t('order.error.connectionDropped', { message: err.message });
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      alert(errorMessage);
      setUploading(false);
      setUploadProgress(0);
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <div className="order-form-page">
      <Header />
      
      {/* Upload Progress Preloader */}
      {uploading && (
        <div className="upload-preloader-overlay">
          <div className="upload-preloader-content">
            <div className="progress-ring-container">
              <svg className="progress-ring" viewBox="0 0 120 120">
                <circle className="progress-ring__track" cx="60" cy="60" r={PROGRESS_RING_RADIUS} />
                <circle
                  className="progress-ring__bar"
                  cx="60" cy="60" r={PROGRESS_RING_RADIUS}
                  strokeDasharray={PROGRESS_RING_CIRCUMFERENCE}
                  strokeDashoffset={PROGRESS_RING_CIRCUMFERENCE * (1 - uploadProgress / 100)}
                />
              </svg>
              <div className="progress-ring__label">{Math.round(uploadProgress)}%</div>
            </div>
            <h2 className="upload-message-title">{t('order.uploadTitle')}</h2>
            <p className="upload-message-warning">
              {t('order.uploadWarning')}
            </p>
          </div>
        </div>
      )}

      {/* Manual Upload - Success Popup */}
      {orderSubmitted && isManualUploadOrder && (
        <div className="manual-success-overlay">
          <div className="manual-success-modal">
            <div className="success-icon">✅</div>
            <h3>{t('order.manualSuccessTitle')}</h3>
            <p>
              {failedPhotos.length > 0
                ? t('order.manualSuccessPartial', { done: totalPhotos - failedPhotos.length, total: totalPhotos })
                : t('order.manualSuccessAllOk')}
            </p>

            <div className="images-reminder">
              <p className="reminder-text">
                {t('order.photoLinkReady')}
              </p>
            </div>

            {failedPhotos.length > 0 && (
              <div className="images-reminder" style={{ background: '#fff3cd', border: '1px solid #ffe69c' }}>
                <p className="reminder-text" style={{ color: '#856404' }}>
                  {t('order.someFailedManual', { count: failedPhotos.length, names: failedPhotos.map(f => f.originalName).join(', ') })}
                </p>
              </div>
            )}

            <div className="manual-success-actions">
              <a
                href={orderPhotoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-fallback-btn"
              >
                {t('order.viewPhotos')}
              </a>

              <button
                type="button"
                onClick={handleCopyPhotoLink}
                className="whatsapp-fallback-btn"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                {linkCopied ? t('order.linkCopied') : t('order.copyLink')}
              </button>
            </div>

            <button
              onClick={() => navigate("/")}
              className="back-home-btn"
            >
              {t('order.backHome')}
            </button>
          </div>
        </div>
      )}

      <div className="order-container">
        {inAppBrowser && (
          <div style={{
            background: '#fff3cd', border: '1px solid #ffe69c', borderRadius: '8px',
            padding: '12px 16px', margin: '0 0 16px', color: '#856404', fontSize: '0.95rem',
          }}>
            {t('order.inAppWarningBefore', { browser: inAppBrowser })}
            {' '}<strong>{t('order.inAppOpenBrowser')}</strong> {t('order.inAppWarningAfter')}
          </div>
        )}
        <div className="order-content">
          <div className="order-summary">
            <h2>{t('order.summaryTitle')}</h2>

            <div className="summary-card">
              <div className="summary-header">
                <h3>{orderData.product?.name || 'Produkt'}</h3>
                <div style={{ textAlign: 'right' }}>
                  {getEffectiveDiscount() > 0 && (
                    <div style={{ fontSize: '13px', color: '#999', textDecoration: 'line-through' }}>
                      {((orderData.totalPrice || 0) + getShippingCost(formData.country)).toFixed(2)}€
                    </div>
                  )}
                  <span className="summary-price" style={{ color: getEffectiveDiscount() > 0 ? '#38a169' : 'inherit' }}>
                    {(getDiscountedProductPrice() + getShippingCost(formData.country)).toFixed(2)}€
                  </span>
                  {getEffectiveDiscount() > 0 && (
                    <div style={{ fontSize: '12px', color: '#38a169', fontWeight: '700' }}>
                      {t('order.discountApplied', { pct: Math.round(getEffectiveDiscount() * 100) })}
                    </div>
                  )}
                  {formData.country && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      {t('order.shippingLine', { price: getDiscountedProductPrice(), shipping: getShippingCost(formData.country) })}
                    </div>
                  )}
                </div>
              </div>

              <div className="summary-details">
                {orderData.selectedPages && (
                  <div className="summary-item">
                    <span className="summary-label">{t('order.pagesCount')}</span>
                    <span className="summary-value">
                      {orderData.selectedPages?.label || `${orderData.selectedPages?.pages} ${t('wa.pagesWord')}`}
                    </span>
                  </div>
                )}

                {/* Show different info for PhotoBook vs regular orders */}
                {orderData.isPhotoBook ? (
                  <div className="summary-item">
                    <span className="summary-label">{t('order.photosInPages')}</span>
                    <span className="summary-value">
                      {t('order.photosCountUnit', { n: orderData.photosInPages || 0 })}
                    </span>
                  </div>

                ) : (
                  <div className="summary-item">
                    <span className="summary-label">{t('order.photosUploaded')}</span>
                    <span className="summary-value">
                      {t('order.photosCountUnit', { n: orderData.uploadedImages?.length || 0 })}
                    </span>
                  </div>
                )}
              </div>

              {/* PhotoBook order - show page info */}
              {orderData.isPhotoBook && orderData.pages && (
                <div className="summary-photobook">
                  <h4>{t('order.photoBookDesigned')}</h4>
                  <p>{t('order.photoBookDesignedDesc', { pages: orderData.pages.length, photos: orderData.photosInPages || 0 })}</p>
                  <p className="pdf-note">{t('order.pdfAutoNote')}</p>
                       <span className="posta">{t('order.shippingNote')}</span>


                </div>
              )}

              {/* Regular order - show photo previews */}
              {!orderData.isPhotoBook && (orderData.uploadedImages?.length || 0) > 0 && (
                <div className="summary-images">
                  <h4>{t('order.yourPhotos')}</h4>
                  <div className="summary-images-grid">
                    {orderData.uploadedImages?.slice(0, 4).map((img, index) => (
                      <img
                        key={index}
                        src={img.url}
                        alt={`Preview ${index + 1}`}
                      />
                    ))}
                    {(orderData.uploadedImages?.length || 0) > 4 && (
                      <div className="more-images">
                        +{(orderData.uploadedImages?.length || 0) - 4}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="order-form-section">
            <h2>{t('order.yourDataTitle')}</h2>

            <form onSubmit={handleSubmit} className="order-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">{t('order.firstName')}</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? "error" : ""}
                    placeholder={t('order.firstNamePlaceholder')}
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">{t('order.lastName')}</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? "error" : ""}
                    placeholder={t('order.lastNamePlaceholder')}
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="country">{t('order.country')}</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={errors.country ? "error" : ""}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: errors.country ? '1px solid #e53e3e' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    backgroundColor: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">{t('order.countrySelect')}</option>
                  <option value="Kosovë">{t('order.countryKosovo')}</option>
                  <option value="Shqipëri">{t('order.countryAlbania')}</option>
                  <option value="Maqedoni e Veriut">{t('order.countryMacedonia')}</option>
                </select>
                {errors.country && (
                  <span className="error-message">{errors.country}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="city">{t('order.city')}</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={errors.city ? "error" : ""}
                  placeholder={t('order.cityPlaceholder')}
                />
                {errors.city && (
                  <span className="error-message">{errors.city}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="address">{t('order.address')}</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={errors.address ? "error" : ""}
                  placeholder={t('order.addressPlaceholder')}
                />
                {errors.address && (
                  <span className="error-message">{errors.address}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">{t('order.phone')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? "error" : ""}
                  placeholder="+383 XX XXX XXX"
                />
                {errors.phone && (
                  <span className="error-message">{errors.phone}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="comments">{t('order.comments')}</label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  placeholder={t('order.commentsPlaceholder')}
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="promotionCode">{t('order.promoLabel')}</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    id="promotionCode"
                    value={promotionCode}
                    onChange={(e) => {
                      setPromotionCode(e.target.value);
                      if (promoValid !== null) {
                        setPromoValid(null);
                        setPromoMessage('');
                        setDiscount(0);
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePromoCode(); } }}
                    placeholder={t('order.promoPlaceholder')}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={handlePromoCode}
                    style={{
                      padding: '0 20px',
                      background: '#2d3748',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {t('order.apply')}
                  </button>
                </div>
                {promoMessage && (
                  <span style={{
                    display: 'block',
                    marginTop: '6px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: promoValid ? '#38a169' : '#e53e3e'
                  }}>
                    {promoValid ? '✅ ' : '❌ '}{promoMessage}
                  </span>
                )}
              </div>

              {!orderSubmitted && !isManualUploadOrder && (
                <div className="form-group">
                  <label>{t('order.paymentMethod')}</label>
                  <div className="payment-method-grid">
                    <label className={`payment-method-card${paymentMethod === "cash" ? " selected" : ""}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={paymentMethod === "cash"}
                        onChange={() => setPaymentMethod("cash")}
                      />
                      {/* <span className="payment-method-icon" aria-hidden="true">💵</span> */}
                      <span className="payment-method-text">
                        <span className="payment-method-title">{t('order.payCash')}</span>
                        <span className="payment-method-subtitle">{t('order.payCashSub')}</span>
                      </span>
                      <span className="payment-method-check" aria-hidden="true">✓</span>
                    </label>

                    <label className={`payment-method-card${paymentMethod === "card" ? " selected" : ""}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={paymentMethod === "card"}
                        onChange={() => setPaymentMethod("card")}
                      />
                      {/* <span className="payment-method-icon" aria-hidden="true">💳</span> */}
                      <span className="payment-method-text">
                        <span className="payment-method-title">
                          {t('order.payOnline')}
                          {onlineDiscount.enabled && onlineDiscount.percent > 0 && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '700', color: '#38a169', background: 'rgba(56,161,105,0.12)', padding: '2px 7px', borderRadius: '999px' }}>
                              {t('order.payOnlineDiscountBadge', { pct: Math.round(onlineDiscount.percent) })}
                            </span>
                          )}
                        </span>
                        <span className="payment-method-subtitle">{t('order.payOnlineSub')}</span>
                        <span className="payment-brand-row">
                          <VisaMark />
                          <MastercardMark />
                          <ApplePayMark />
                        </span>
                      </span>
                      <span className="payment-method-check" aria-hidden="true">✓</span>
                    </label>
                  </div>
                  <p className="payment-security-note"> {t('order.paymentSecurityNote')}</p>
                </div>
              )}

              {!orderSubmitted ? (
                <>
                  <button
                    type="submit"
                    className="submit-order-btn"
                    disabled={uploading}
                    style={{
                      opacity: uploading ? 0.6 : 1,
                      cursor: uploading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {uploading
                      ? (orderData.isPhotoBook
                          ? t('order.processingPhotoBook')
                          : t('order.processingPhotos'))
                      : (isManualUploadOrder
                          ? t('order.sendManual')
                          : (paymentMethod === "card" ? t('order.continueToPay') : ` ${t('order.sendWhatsApp')}`))}
                  </button>

                  {uploading && (
                    <div style={{
                      textAlign: 'center',
                      marginTop: '10px',
                      color: '#ff8c00',
                      fontWeight: 'bold'
                    }}>
                      {orderData.isPhotoBook
                        ? t('order.generatingPdf', { pages: orderData.pages?.length || 0 })
                        : t('order.uploadingPhotosCount', { n: orderData.uploadedImages?.length || 0 })}
                    </div>
                  )}

                  <p className="form-note">
                    {isManualUploadOrder
                      ? t('order.noteManual')
                      : paymentMethod === "card"
                        ? t('order.noteCard')
                        : t('order.noteWhatsApp')}
                  </p>
                </>
              ) : isManualUploadOrder ? null : (
                <div className="success-message-box">
                  <div className="success-icon">✅</div>
                  <h3>{t('order.successTitle')}</h3>
                  <p>{t('order.successOpened')}</p>

                  {orderData.isPhotoBook ? (
                    <div className="images-reminder">
                      <p className="reminder-text">
                        📄 <strong>{t('order.pdfSentLabel')}</strong> {t('order.pdfSentDesc', { pages: orderData.pages?.length || 0 })}
                      </p>
                    </div>
                  ) : (orderData.uploadedImages?.length || 0) > 0 && (
                    <div className="images-reminder">
                      <p className="reminder-text">
                        📸 <strong>{t('order.remindSendLabel')}</strong>{" "}
                        {t('order.remindSendDesc', { n: orderData.uploadedImages?.length || 0 })}
                      </p>
                    </div>
                  )}

                  {failedPhotos.length > 0 && (
                    <div className="images-reminder" style={{ background: '#fff3cd', border: '1px solid #ffe69c' }}>
                      <p className="reminder-text" style={{ color: '#856404' }}>
                        {t('order.someFailedBackup', { count: failedPhotos.length, names: failedPhotos.map(f => f.originalName).join(', ') })}
                      </p>
                    </div>
                  )}

                  <p className="small-text">
                    {t('order.ifNotOpened')}
                  </p>

                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-fallback-btn"
                  >
                    {t('order.openWhatsAppManually')}
                  </a>

                  <button
                    onClick={() => navigate("/")}
                    className="back-home-btn"
                  >
                    {t('order.backHome')}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
